import React, { useEffect, useRef } from 'react';
import * as R from 'ramda';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// Utils
import { func, number, string } from 'prop-types';
import { useIntersect } from 'hooks';
import { CardBody, CardFooter, CardHeader } from './card-components';

const CarrierAuthCard = ({
  cardHeight,
  carrierAuthorizationId,
  authorizationId: uniqueId = carrierAuthorizationId,
  fein,
  handleRemove,
  index,
  insuranceExpiration,
  lastUsed,
  nameShort,
  onEdit,
  setCardHeight,
  state,
  viewBy,
}) => {
  // any function ran here will be evaluated on every rerender, regardless if hideCard is true or false
  // please put any specific logic for components inside the component to ensure it is only ran when hideCard is false
  // this is especially important when 188+ cards are rendered, as you are evaluating a signficant amount of logic
  const { entry, setNode } = useIntersect({ rootMargin: '300px' });
  const node = useRef();

  useEffect(() => {
    setNode(node.current);
  }, [setNode]);

  // since the first 10 cards are always rendered, we can pull the card height off the first one right away
  // allows us to make future changes to the cards without worrying about keeping the card heights
  useEffect(() => {
    if (index === 0) setCardHeight(node.current.getBoundingClientRect().height);
  }, [index, setCardHeight]);

  const { intersectionRatio, isIntersecting } = entry;
  // show the first 10 cards on initial render, when intersectionRatio is undefined
  // otherwise, only show if intersectionRatio > 0
  // keeps from showing blank cards on initial load
  // isIntersecting fixes a mobile bug that causes cards just outside the parent to not render
  const hideCard =
    (R.isNil(intersectionRatio) && index > 10) || (intersectionRatio === 0 && !isIntersecting);

  return (
    <Card
      ref={node}
      sx={{
        height: !hideCard && cardHeight ? cardHeight : 'auto',
        minHeight: 234,
      }}
      variant="loading"
    >
      <CardContent variant="loading">
        {!hideCard && (
          <>
            <CardHeader fein={fein} title={nameShort} viewBy={viewBy} />
            <CardBody insuranceExpiration={insuranceExpiration} lastUsed={lastUsed} state={state} />
            <CardFooter
              // Use whichever ID value exists, prevents proptype errors
              carrierAuthorizationId={uniqueId}
              // eslint-disable-next-line no-console
              onEdit={() => onEdit(uniqueId)}
              onRemove={() => handleRemove(uniqueId, nameShort)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

CarrierAuthCard.propTypes = {
  authorizationId: number,
  cardHeight: number.isRequired,
  carrierAuthorizationId: number,
  fein: string,
  handleRemove: func.isRequired,
  index: number.isRequired,
  insuranceExpiration: string,
  lastUsed: string,
  nameShort: string.isRequired,
  onEdit: func.isRequired,
  setCardHeight: func.isRequired,
  state: string,
  viewBy: string.isRequired,
};

CarrierAuthCard.defaultProps = {
  authorizationId: undefined,
  carrierAuthorizationId: undefined,
  fein: null,
  insuranceExpiration: null,
  lastUsed: null,
  state: null,
};

export default CarrierAuthCard;
